import {useEffect} from "react";
import {useLocation} from "react-router-dom";
import {subscribeConsent} from "consent/consentStore";
import {reportPageView} from "./pageView";

/**
 * Reports a page view on every route change (kitecorp/transferify-api-service#1929).
 *
 * A thin adapter by mandate (epic #26): what a page view SAYS, which segments are redacted and whether
 * consent allows it to be sent at all are {@link reportPageView}'s decisions, in a React-free module.
 *
 * ── Why the app SHELL and not RootLayout ─────────────────────────────────────────────────────────
 * gtag sent this from the tag `RootLayout` started, which covered every route — public, private and
 * the recipient pages alike. This has to cover the same set, so it is mounted on the shell rather than
 * beside `useRedditPixel`'s route scope, which is narrower on purpose. `RootLayout` is where
 * `useAnalyticsConsent` lives and would have been the closer mirror of the old mount point, but it
 * renders the router's provider tree rather than sitting inside it, so it cannot see a route change.
 * The shell can, it survives every one of them, and it is where the other route-change reporter
 * already lives.
 *
 * ── Why the pathname alone is the dependency ─────────────────────────────────────────────────────
 * A page view carries no query string (see {@link reportPageView}), so a search-parameter change
 * reports the identical address. Keying on the full location would count the same page twice every
 * time a dialog wrote a parameter.
 *
 * ── Why a consent change reports too ─────────────────────────────────────────────────────────────
 * A visitor who presses Accept on the page they landed on has granted consent without navigating
 * anywhere, and that load is a page view: gtag reported it because `config` ran at the moment the tag
 * started. Route changes alone would silently lose the first page of every consenting visit — which is
 * most of them. Counting it once is {@link reportPageView}'s job, not this hook's.
 *
 * ── Why the report waits a frame ─────────────────────────────────────────────────────────────────
 * The document title is committed by react-helmet-async, through `vite-react-ssg`'s `Head`, on an
 * animation frame — its `defer` default. Reading `document.title` in this effect would therefore
 * report the PREVIOUS page's title on every navigation. Helmet schedules its frame from a commit
 * deeper in the tree than this effect, so the callback below runs after it.
 */
export function usePageViewReporting(): void {
	const {pathname} = useLocation();

	useEffect(() => {
		// The browser's own address, so the reported origin is the host being browsed rather than a
		// canonical one (ui#1517) — and so the query and fragment are dropped by the rule module that
		// owns that decision, not by whatever this hook happened to pass.
		const report = () => reportPageView(window.location.href, document.title);
		const framed = typeof requestAnimationFrame === "function";
		let frame = 0;
		const schedule = () => {
			if (!framed) {
				report();
				return;
			}
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(report);
		};
		schedule();
		const unsubscribe = subscribeConsent(schedule);
		return () => {
			unsubscribe();
			if (framed) {
				cancelAnimationFrame(frame);
			}
		};
	}, [pathname]);
}
